import { asString } from '../parser';
import type { Rule } from './types';

const SECRET_PATTERNS: Array<{ pattern: RegExp; label: string }> = [
  { pattern: /sk-[A-Za-z0-9]{20,}/, label: 'OpenAI-style key (sk-…)' },
  { pattern: /\bAKIA[0-9A-Z]{16}\b/, label: 'AWS access key (AKIA…)' },
  { pattern: /\bghp_[A-Za-z0-9]{30,}\b/, label: 'GitHub personal token (ghp_…)' },
  { pattern: /\bxox[bp]-[A-Za-z0-9-]{10,}\b/, label: 'Slack token (xoxb-/xoxp-)' },
];

function containsAngleBrackets(value: unknown): boolean {
  if (typeof value === 'string') return /[<>]/.test(value);
  if (Array.isArray(value)) return value.some(containsAngleBrackets);
  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some(containsAngleBrackets);
  }
  return false;
}

const ALLOWED_TOOLS_RE = /^[A-Za-z][\w]*(\([^)]*\))?(\s+[A-Za-z][\w]*(\([^)]*\))?)*$/;

export const safetyRules: Rule[] = [
  {
    id: 'safety.no-xml-frontmatter',
    category: 'safety',
    severity: 'fail',
    weight: 3,
    title: 'No XML tags anywhere in frontmatter',
    appliesTo: 'both',
    run(ctx) {
      if (containsAngleBrackets(ctx.parsed.frontmatter)) {
        return {
          status: 'fail',
          message: 'Found `<` or `>` in frontmatter values.',
          fix: 'Remove angle brackets. Frontmatter is injected into Claude\'s system prompt.',
        };
      }
      return { status: 'pass', message: 'Clean frontmatter.' };
    },
  },
  {
    id: 'safety.no-secrets',
    category: 'safety',
    severity: 'warn',
    weight: 2,
    title: 'No obvious secrets in body',
    appliesTo: 'both',
    run(ctx) {
      const hits = SECRET_PATTERNS.filter(({ pattern }) => pattern.test(ctx.parsed.body));
      if (hits.length > 0) {
        return {
          status: 'fail',
          message: `Possible leaked credentials: ${hits.map((h) => h.label).join(', ')}.`,
          fix: 'Replace real values with placeholders like `<YOUR_API_KEY>` (in body docs only, not frontmatter).',
        };
      }
      return { status: 'pass', message: 'No secret patterns detected.' };
    },
  },
  {
    id: 'meta.compatibility-length',
    category: 'safety',
    severity: 'info',
    weight: 1,
    title: '`compatibility` field length',
    appliesTo: 'both',
    run(ctx) {
      const c = asString(ctx.parsed.frontmatter.compatibility);
      if (!c) return { status: 'skipped', message: 'No compatibility field.' };
      if (c.length > 500) {
        return {
          status: 'warn',
          message: `\`compatibility\` is ${c.length} chars (max 500).`,
          fix: 'Trim the compatibility field.',
        };
      }
      return { status: 'pass', message: `${c.length} chars.` };
    },
  },
  {
    id: 'meta.allowed-tools-format',
    category: 'safety',
    severity: 'info',
    weight: 1,
    title: '`allowed-tools` uses expected format',
    appliesTo: 'both',
    run(ctx) {
      const v = ctx.parsed.frontmatter['allowed-tools'];
      if (!v) return { status: 'skipped', message: 'No allowed-tools field.' };
      const s = asString(v);
      if (!s) return { status: 'skipped', message: 'allowed-tools not a string.' };
      if (!ALLOWED_TOOLS_RE.test(s)) {
        return {
          status: 'warn',
          message: '`allowed-tools` does not match `Tool(pattern:*) Tool …` syntax.',
          fix: 'Example: `"Bash(python:*) Bash(npm:*) WebFetch"`.',
        };
      }
      return { status: 'pass', message: 'Format looks valid.' };
    },
  },
  {
    id: 'meta.has-version',
    category: 'safety',
    severity: 'info',
    weight: 1,
    title: '`metadata.version` set',
    appliesTo: 'both',
    run(ctx) {
      const meta = ctx.parsed.frontmatter.metadata;
      if (meta && typeof meta === 'object' && 'version' in (meta as Record<string, unknown>)) {
        return { status: 'pass', message: 'Versioned.' };
      }
      return { status: 'pass', message: 'No version set (optional).' };
    },
  },
];
